// ─── Generationstiefe ────────────────────────────────────────────────────────
//
// „Wer ist wessen Kind" ist die einzige Ordnung, die ein Stammbaum immer hat.
// Geburtsdaten fehlen in der Praxis bei einem guten Teil der Personen; die
// Abstammung nicht – sonst wäre die Person gar nicht im Baum.
//
// Zwei Stellen brauchen das: der Sternenhimmel für seine Tiefenachse und die
// Chronik für ihre Kapitel. Deshalb steht es hier und nicht in einer der
// beiden Anwendungen.

/**
 * Generationstiefe je Person: ohne Eltern 0, sonst tiefster Elternteil + 1.
 *
 * „Tiefster" und nicht „erster": stehen die Eltern in verschiedenen
 * Generationen – bei angeheirateten Zweigen der Normalfall –, zählt die
 * spätere. Sonst überholte ein Kind seinen eigenen Großvater.
 */
export function berechneGenerationen(
  ids: string[],
  eltern: Map<string, string[]>,
): Map<string, number> {
  const gen = new Map<string, number>(ids.map((id) => [id, 0]));

  // Wiederholtes Angleichen statt Topologie-Sortierung: die Elternangaben
  // können im Einzelfall einen Zyklus enthalten (jemand ist sein eigener
  // Vorfahre – ein Tippfehler genügt). Die Rundenzahl ist begrenzt, damit das
  // nichts zum Stillstand bringt.
  for (let runde = 0; runde < ids.length; runde++) {
    let veraendert = false;
    for (const id of ids) {
      const e = eltern.get(id);
      if (!e || e.length === 0) continue;
      let hoechste = -1;
      for (const p of e) {
        const g = gen.get(p);
        if (g !== undefined && g > hoechste) hoechste = g;
      }
      if (hoechste >= 0 && hoechste + 1 > (gen.get(id) as number)) {
        gen.set(id, hoechste + 1);
        veraendert = true;
      }
    }
    if (!veraendert) break;
  }

  return gen;
}

// ─── Durchlauf durch den ganzen Baum ─────────────────────────────────────────
//
// berechneGenerationen oben beantwortet die Frage „wie viele Generationen von
// Vorfahren stehen über dieser Person" – sie rechnet je Person aus deren
// EIGENEN Vorfahren. Für eine Tiefenachse ist das richtig.
//
// Für ein Stammbuch ist es falsch, und zwar auf zwei Arten. Beide zeigen sich,
// sobald die Eltern einer eingeheirateten Person mit erfasst sind:
//
//   * Der Schwiegervater steht in Kapitel 1. Er hat selbst keine Vorfahren im
//     Baum, also ist seine Tiefe 0 – gleichgültig, dass seine Tochter in die
//     vierte Generation geheiratet hat.
//   * Und er zieht seine Tochter mit nach oben: sie hat nun Eltern, ihre Tiefe
//     ist Vater + 1 – und damit steht sie in einem anderen Kapitel als ihr
//     eigener Ehemann.
//
// Ein Stammbuch braucht deshalb einen Durchlauf durch den GANZEN Baum, bei dem
// jede Person ihre Ebene daher bekommt, wo sie erreicht wird.

export interface Verwandtschaft {
  /** Kind → Eltern. */
  eltern: Map<string, string[]>;
  /** Person → Ehe- und Lebenspartner. */
  partner: Map<string, string[]>;
}

/**
 * Ordnet jede Person einer Familiengeneration zu – durch einen Durchlauf von
 * oben nach unten statt durch Rechnen je Person.
 *
 * Der Baum wird als ein einziges Geflecht gelesen, mit zwei Arten von Kanten:
 *
 *   Eltern → Kind      eine Ebene tiefer
 *   Partner ↔ Partner  dieselbe Ebene
 *
 * Von der ältesten Person aus wird das Geflecht abgeschritten: wer über ein
 * Kind erreicht wird, kommt eine Ebene tiefer, wer über eine Ehe erreicht wird,
 * auf dieselbe, und rückwärts von einem Kind zum Elternteil eine Ebene höher.
 * Am Ende wird so verschoben, dass die oberste Ebene 0 ist.
 *
 * `reihenfolge` bestimmt, wo begonnen wird – sinnvollerweise nach Geburtsjahr
 * aufsteigend. Zerfällt der Baum in unverbundene Teile, beginnt jeder Teil bei
 * seiner eigenen ältesten Person; etwas anderes wäre erfunden, denn zwischen
 * ihnen gibt es keine Verbindung, aus der sich ein Abstand ergäbe.
 *
 * Trifft der Durchlauf über zwei Wege auf dieselbe Person – bei einer
 * Vettern-Heirat der Normalfall –, gilt der zuerst gefundene Weg. Beide sind
 * begründbar, und ein Buch braucht eine Entscheidung, keine zwei.
 */
export function generationenDurchlauf(
  reihenfolge: string[],
  { eltern, partner }: Verwandtschaft,
): Map<string, number> {
  // Gegenrichtung: Elternteil → Kinder.
  const kinder = new Map<string, string[]>();
  for (const [kind, es] of eltern) {
    for (const e of es) {
      if (!kinder.has(e)) kinder.set(e, []);
      kinder.get(e)!.push(kind);
    }
  }

  const ebene = new Map<string, number>();

  for (const start of reihenfolge) {
    if (ebene.has(start)) continue; // schon über einen anderen Teil erreicht

    // Ein unverbundener Teil des Baums: hier beginnt der Durchlauf neu.
    const teil: string[] = [start];
    ebene.set(start, 0);
    const schlange = [start];

    while (schlange.length > 0) {
      const p = schlange.shift() as string;
      const e = ebene.get(p) as number;

      const weiter = (nachbar: string, dort: number) => {
        if (ebene.has(nachbar)) return; // der zuerst gefundene Weg gilt
        ebene.set(nachbar, dort);
        teil.push(nachbar);
        schlange.push(nachbar);
      };

      for (const k of kinder.get(p) ?? []) weiter(k, e + 1);
      for (const v of eltern.get(p) ?? []) weiter(v, e - 1);
      for (const q of partner.get(p) ?? []) weiter(q, e);
    }

    // Der Start war nicht zwingend der oberste – von ihm aus ging es auch nach
    // oben. Diesen Teil so verschieben, dass seine oberste Ebene 0 ist.
    let kleinste = 0;
    for (const id of teil) kleinste = Math.min(kleinste, ebene.get(id) as number);
    if (kleinste !== 0) {
      for (const id of teil) ebene.set(id, (ebene.get(id) as number) - kleinste);
    }
  }

  return ebene;
}

/**
 * Der Median – robuster gegen einzelne Ausreißer als der Durchschnitt.
 *
 * Bei gerader Anzahl wird zwischen den beiden mittleren Werten gemittelt. Der
 * sonst übliche Griff zum oberen der beiden verschöbe die geschätzten
 * Jahrgänge: bei einem Ehepaar von 1848 und 1884 käme 1884 heraus statt 1866.
 */
export function median(werte: number[]): number | null {
  if (werte.length === 0) return null;
  const s = [...werte].sort((a, b) => a - b);
  const m = Math.floor(s.length / 2);
  return s.length % 2 === 1 ? s[m] : (s[m - 1] + s[m]) / 2;
}

/** Ordnungszahl einer Generation, wie sie in Überschriften steht. */
export function generationsTitel(index: number): string {
  const namen = [
    'Erste Generation',
    'Zweite Generation',
    'Dritte Generation',
    'Vierte Generation',
    'Fünfte Generation',
    'Sechste Generation',
    'Siebte Generation',
    'Achte Generation',
    'Neunte Generation',
    'Zehnte Generation',
  ];
  return namen[index] ?? `${index + 1}. Generation`;
}
